import { useEffect, useMemo, useRef } from 'react';
import { SurveyCreatorComponent, SurveyCreator } from 'survey-creator-react';
import {
  editorLocalization,
  type SurveyCreatorModel,
  type UploadFileEvent,
} from 'survey-creator-core';
import { surveyLocalization } from 'survey-core';
import 'survey-core/survey-core.css';
import 'survey-creator-core/survey-creator-core.css';
// Side-effect locale registrations. The SurveyJS bundle stores locales on a
// global registry; importing the module registers the strings under the
// matching key (`pt` for the Creator UI, `pt-br` for the candidate-side
// renderer). After this we just flip the active locale.
import 'survey-creator-core/i18n/portuguese';
import 'survey-core/i18n/portuguese-br';
import { uploadFormAsset } from '../forms-mutations';
import { creatorOptionsForAlignment } from '@/features/programs/program-alignment-form/alignment-restrictions';
import {
  bindAlignmentRestrictions,
  type AlignmentBinding,
} from '@/features/programs/program-alignment-form/bind-alignment-restrictions';

editorLocalization.currentLocale = 'pt';
surveyLocalization.currentLocale = 'pt-br';

export interface SurveyCreatorWrapperProps {
  /** Form id — routes the asset upload to `POST /forms/:id/assets`. */
  formId: string;
  /** Initial SurveyJS JSON model (`{ pages: [...] }`). Hydrated once on mount. */
  initialDefinition: unknown;
  /** Fires whenever the operator edits the survey. The container persists this on Save. */
  onChange: (definition: unknown) => void;
  /**
   * DEV-269 — modo RESTRITO (formulário de alinhamento de vagas). Quando
   * presente: sem aba JSON, sem título do questionário, categorias reduzidas
   * e as regras padrão/adicional/protegido ligadas por instância a partir de
   * `restrictions` (vindas da API). Ausente = comportamento idêntico ao do
   * construtor de formulários de candidato (SC-16). Capturado no mount.
   */
  restriction?: AlignmentBinding;
}

/**
 * Thin wrapper around `survey-creator-react`.
 *
 * - Mounts a single `SurveyCreator` instance, seeded from the initial JSON
 *   captured at mount via a ref. Re-renders of the parent (which can change
 *   `initialDefinition`'s identity even when the value is equivalent) do
 *   NOT reset the editor — that would discard mid-edit changes.
 * - Subscribes to `onModified` (the broad change event) and forwards the
 *   current `JSON` snapshot to the parent on every edit.
 * - Imports SurveyJS CSS at the wrapper level so callers don't need to.
 *
 * Configuration: shows the standard tabs except `translations` and `themes`
 * (out of scope for the recruiter MVP). Toolbox auto-registers all
 * built-in question types.
 */
export function SurveyCreatorWrapper({
  formId,
  initialDefinition,
  onChange,
  restriction,
}: SurveyCreatorWrapperProps) {
  // Captures the seed value at mount; subsequent `initialDefinition` prop
  // changes are intentionally ignored to keep editor state stable.
  const initialDefinitionRef = useRef(initialDefinition);
  const formIdRef = useRef(formId);
  formIdRef.current = formId;
  const restrictionRef = useRef(restriction);
  restrictionRef.current = restriction;
  const unbindRef = useRef<(() => void) | null>(null);

  const creator = useMemo(() => {
    const restricted = restrictionRef.current !== undefined;
    const instance = new SurveyCreator(
      restricted
        ? creatorOptionsForAlignment()
        : {
            showLogicTab: true,
            showJSONEditorTab: true,
            showThemeTab: false,
            showTranslationTab: false,
            isAutoSave: false,
          }
    );
    if (restricted) {
      // AC-28/28b — título fixo no cabeçalho da página; o canvas começa na
      // primeira página.
      instance.showSurveyTitle = false;
      instance.allowEditSurveyTitle = false;
    }
    // DEV-269 — as regras do modo restrito são ligadas ANTES de carregar o
    // JSON: o property grid do objeto selecionado por padrão (o formulário
    // inteiro) é criado no `instance.JSON = …`, e `onPropertyGridSurveyCreated`
    // precisa já estar ouvindo para reduzir a barra de categorias (AC-29b).
    // Por instância, nunca global. O binding é capturado no mount: trocar
    // `restrictions` depois não religa.
    const binding = restrictionRef.current;
    const unbind = binding
      ? bindAlignmentRestrictions(instance, {
          restrictions: binding.restrictions,
          onRejected: (message) => restrictionRef.current?.onRejected(message),
          onConfirmDeletePage: (page, deletePage) =>
            restrictionRef.current?.onConfirmDeletePage(page, deletePage),
        })
      : null;
    instance.JSON = (initialDefinitionRef.current as object) ?? { pages: [] };
    if (binding) {
      // Reconstrói o property grid da seleção inicial com os handlers ativos.
      instance.selectElement(instance.survey);
    }
    unbindRef.current = unbind;
    return instance;
  }, []);

  useEffect(() => {
    return () => {
      unbindRef.current?.();
      unbindRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handle = () => {
      onChange(creator.JSON);
    };
    creator.onModified.add(handle);
    return () => {
      creator.onModified.remove(handle);
    };
  }, [creator, onChange]);

  // Intercept file uploads (Image question type, banner/logo properties) and
  // push them through our BFF instead of letting SurveyJS inline as base64.
  // Without this every image bloats the schema and PATCH /forms/:id 413s.
  useEffect(() => {
    const handle = (_sender: SurveyCreatorModel, options: UploadFileEvent) => {
      const file = options.files[0];
      if (!file) {
        options.callback('error', 'Nenhum arquivo selecionado');
        return;
      }
      void uploadFormAsset(formIdRef.current, file)
        .then(({ url }) => options.callback('success', url))
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Falha no upload';
          options.callback('error', message);
        });
    };
    creator.onUploadFile.add(handle);
    return () => {
      creator.onUploadFile.remove(handle);
    };
  }, [creator]);

  return (
    <div
      className={
        restriction ? 'h-full min-h-[600px] alignment-form-restricted' : 'h-full min-h-[600px]'
      }
    >
      <SurveyCreatorComponent creator={creator} />
    </div>
  );
}
